// src/features/records/components/GalleryView/hooks/useJustifiedGrid.js
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { computeJustifiedRows } from '../layout/computeJustifiedRows';

const DEFAULT_ROW_HEIGHT = 200;

export function useJustifiedGrid({
  items,
  gap = 4,
  targetRowHeight = DEFAULT_ROW_HEIGHT,
  rowHeightForWidth,
  minScale = 0.75,
  maxScale = 1.35,
  justifyLastRow = false,
  widowLimit = 2,
  overscan = 6,
} = {}) {
  const parentRef = useRef(null);
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (!parentRef.current) return undefined;

    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width: nextWidth } = entry.contentRect;
      const rounded = Math.round(nextWidth);
      setWidth((prev) => (prev === rounded ? prev : rounded));
    });

    observer.observe(parentRef.current);

    return () => observer.disconnect();
  }, []);

  const effectiveTargetHeight = useMemo(() => {
    if (typeof rowHeightForWidth === 'function') {
      const next = rowHeightForWidth(width);
      if (Number.isFinite(next) && next > 0) {
        return next;
      }
    }
    return targetRowHeight;
  }, [rowHeightForWidth, targetRowHeight, width]);

  const rows = useMemo(() => {
    if (!width) return [];
    return computeJustifiedRows(items ?? [], width, {
      gap,
      targetRowHeight: effectiveTargetHeight,
      minScale,
      maxScale,
      justifyLastRow,
      widowLimit,
    });
  }, [items, width, gap, effectiveTargetHeight, minScale, maxScale, justifyLastRow, widowLimit]);

  const getScrollElement = useCallback(() => parentRef.current, []);

  const estimateSize = useCallback(
    (index) => {
      const row = rows[index];
      if (!row) return effectiveTargetHeight + gap;
      return row.height + gap;
    },
    [rows, effectiveTargetHeight, gap]
  );

  const getItemKey = useCallback((index) => rows[index]?.key ?? index, [rows]);

  // TanStack Virtual needs stable option callbacks to avoid render loops.
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement,
    estimateSize,
    overscan,
    getItemKey,
  });

  return {
    parentRef,
    containerWidth: width,
    rows,
    gap,
    rowVirtualizer,
    targetRowHeight: effectiveTargetHeight,
  };
}

export default useJustifiedGrid;
